import React, { useState, useRef, useEffect } from "react";
import { Pencil, Trash2, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const EditPackage = () => {
  const [packages, setPackages] = useState([
    {
      id: 1,
      packageName: "Himalayan Adventure",
      destination: "Nepal",
      country: "Nepal",
      price: 1200,
      numberOfDays: 7,
      numberOfNights: 6,
      validUntil: "2025-12-31",
      description: "Explore the Himalayas with guided treks and local experiences.",
      accommodationDetails: "Lodge stays and base camps",
      mealInclusions: "Breakfast, Dinner",
      sightseeingAndTransfers: "Local guides, mountain transfers",
      exclusions: "Flights, personal insurance",
      priceNote: "Prices are per person",
    },
  ]);

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [editedData, setEditedData] = useState(null);

  const modalRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setSelectedPackage(null); // Close modal
      }
    };

    if (selectedPackage) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedPackage]);


  const handleEdit = (pkg) => {
    setSelectedPackage(pkg);
    setEditedData({ ...pkg });
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this package?")) {
      setPackages((prev) => prev.filter((pkg) => pkg.id !== id));
    }
  };

  const handleChange = (e) => {
    setEditedData({
      ...editedData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSave = () => {
    setPackages((prev) =>
      prev.map((pkg) =>
        pkg.id === editedData.id
          ? {
            ...editedData,
            price: +editedData.price,
            numberOfDays: +editedData.numberOfDays,
            numberOfNights: +editedData.numberOfNights,
          }
          : pkg
      )
    );
    setSelectedPackage(null);
  };

  return (
    <motion.div
      className="max-w-5xl mx-auto p-6 mt-10 bg-slate-900 rounded-xl text-slate-200 shadow-2xl"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className="text-2xl font-bold mb-6">Manage Travel Packages</h2>

      {packages.length === 0 ? (
        <p className="text-slate-400">No packages available.</p>
      ) : (
        <div className="space-y-4">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className="bg-slate-800 rounded-lg p-4 flex justify-between items-start gap-4"
            >
              <div className="flex-1">
                <h3 className="text-xl font-semibold">{pkg.packageName}</h3>
                <p className="text-sm text-slate-400 mb-1">
                  <strong>Destination:</strong> {pkg.destination} ({pkg.country})
                </p>
                <p className="text-sm text-slate-400 mb-1">
                  <strong>Price:</strong> ${pkg.price}
                </p>
                <p className="text-sm text-slate-400 mb-1">
                  <strong>Days/Nights:</strong> {pkg.numberOfDays} / {pkg.numberOfNights}
                </p>
                <p className="text-sm text-slate-400 mb-1">
                  <strong>Valid Until:</strong> {pkg.validUntil}
                </p>
                <p className="text-sm text-slate-400">{pkg.description}</p>
              </div>

              <div className="flex gap-3 items-start">
                <button onClick={() => handleEdit(pkg)} className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg">
                  <Pencil className="w-5 h-5 text-white" />
                </button>
                <button onClick={() => handleDelete(pkg.id)} className="p-2 bg-red-600 hover:bg-red-700 rounded-lg">
                  <Trash2 className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      <AnimatePresence>
        {selectedPackage && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 overflow-auto p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              ref={modalRef}
              className="bg-slate-800 p-6 rounded-xl w-full max-w-lg relative max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 50 }}
              transition={{ duration: 0.3 }}
            >
              <button onClick={() => setSelectedPackage(null)} className="absolute top-3 right-3 text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>

              <h3 className="text-xl font-semibold mb-4">Edit Package</h3>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { name: "packageName", label: "Package Name" },
                  { name: "destination", label: "Destination" },
                  { name: "country", label: "Country" },
                  { name: "price", label: "Price", type: "number" },
                  { name: "numberOfNights", label: "Nights", type: "number" },
                  { name: "numberOfDays", label: "Days", type: "number" },
                  { name: "validUntil", label: "Valid Until", type: "date" },
                ].map(({ name, label, type = "text" }) => (
                  <div key={name}>
                    <label htmlFor={name} className="block text-sm mb-1 text-slate-300">
                      {label}
                    </label>
                    <input
                      id={name}
                      name={name}
                      type={type}
                      value={editedData[name]}
                      onChange={handleChange}
                      className="w-full p-2 rounded-md bg-slate-700 text-white border border-slate-600 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                ))}
              </div>

              <div className="mt-4 space-y-3">
                {[
                  { name: "description", label: "Description" },
                  { name: "accommodationDetails", label: "Accommodation Details" },
                  { name: "mealInclusions", label: "Meal Inclusions" },
                  { name: "sightseeingAndTransfers", label: "Sightseeing & Transfers" },
                  { name: "exclusions", label: "Exclusions" },
                  { name: "priceNote", label: "Price Note" },
                ].map(({ name, label }) => (
                  <div key={name}>
                    <label htmlFor={name} className="block text-sm mb-1 text-slate-300">
                      {label}
                    </label>
                    <textarea
                      id={name}
                      name={name}
                      rows={2}
                      value={editedData[name]}
                      onChange={handleChange}
                      className="w-full p-2 rounded-md bg-slate-700 text-white border border-slate-600 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                ))}
              </div>

              <button
                onClick={handleSave}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg w-full mt-4"
              >
                Save Changes
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default EditPackage;
